import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase, TABLES, isTestMode, mockAssistants } from '../services/supabase';
import { Assistant } from '../types';
import ChatInterface from '../components/ChatInterface';
import { Bot } from 'lucide-react';
import Logo from '../components/dashboard/Logo';

const AssistantChat: React.FC = () => {
    const { uniqueUrl } = useParams<{ uniqueUrl: string }>();
    const [assistant, setAssistant] = useState<Assistant | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [shouldReducePadding, setShouldReducePadding] = useState(false);

    // Use assistant's background image or fallback to stock image
    const backgroundImageUrl = assistant?.background_image_url || "https://plus.unsplash.com/premium_photo-1679769911227-429b4e1b184b?fm=jpg&q=60&w=3000&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D";

    const fetchAssistant = React.useCallback(async () => {
        if (!uniqueUrl) return;

        if (isTestMode) {
            // Use mock data in test mode
            const mockAssistant = mockAssistants.find(a => a.unique_url === uniqueUrl);
            if (mockAssistant) {
                setAssistant(mockAssistant);
            } else {
                setError('Echoo not found');
            }
            setLoading(false);
            return;
        }

        try {
            const { data, error } = await supabase
                .from(TABLES.ASSISTANTS)
                .select('*')
                .eq('unique_url', uniqueUrl)
                .single();

            if (error) throw error;
            setAssistant(data);
        } catch (error) {
            console.error('Error fetching assistant:', error);
            setError('Echoo not found');
        } finally {
            setLoading(false);
        }
    }, [uniqueUrl]);

    useEffect(() => {
        fetchAssistant();
    }, [fetchAssistant]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                    <p className="text-white">Loading Echoo...</p>
                </div>
            </div>
        );
    }

    if (error || !assistant) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-400 via-purple-500 to-pink-500 flex items-center justify-center">
                <div className="text-center max-w-md mx-auto px-4">
                    <div className="bg-white rounded-3xl shadow-lg p-8 border border-gray-100">
                        <Bot className="mx-auto h-16 w-16 text-gray-400 mb-6" />
                        <h2 className="text-2xl font-bold text-gray-900 mb-3">Echoo Not Found</h2>
                        <p className="text-gray-600 leading-relaxed">
                            The Echoo you're looking for doesn't exist or has been removed.
                            Please check the URL and try again.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="h-screen overflow-hidden relative" style={{ backgroundColor: assistant.primary_color }}>
            {/* Background Image */}
            <div
                className={`absolute inset-0 bg-cover bg-center bg-no-repeat w-full ${shouldReducePadding ? 'h-48' : 'h-96'} md:w-1/2 md:h-full transition-all duration-150 ease-in-out bg-image-fade`}
                style={{
                    backgroundImage: `url(${backgroundImageUrl})`,
                    backgroundPosition: 'left center',
                    backgroundSize: 'cover'
                }}
            />

            {/* Header elements overlaid on background image */}
            <div className="absolute top-0 left-0 right-0 z-10 p-4 md:p-6">
                <div className="flex justify-between items-center">
                    <div className="flex items-center">
                        <Logo title="Echoo.ai" variant="light" />
                    </div>
                    <button className="text-white hover:text-gray-200 transition-colors">
                        Sign in
                    </button>
                </div>
            </div>

            {/* Chat Interface */}
            <div className="relative z-20 h-full flex justify-end">
                <div className={`w-full max-w-4xl p-0 md:p-6 h-full ${shouldReducePadding ? 'pt-16' : 'pt-64'} md:pt-6 transition-all duration-150 ease-in-out`}>
                    <div className="bg-white rounded-t-3xl md:rounded-3xl shadow-lg overflow-hidden h-full">
                        <ChatInterface assistant={assistant} onShouldReducePaddingChange={setShouldReducePadding} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AssistantChat;
